import React from 'react';
import styled, { css } from 'styled-components';

import { formatDateTime, isValidDate } from '~/util';

interface AbsoluteTimeProps {
  className?: string;
  timestamp: Date | string;
  preventHover?: boolean;
  alignment?: 'left' | 'right';
}

const Local = styled.div<{ alignment?: 'left' | 'right' }>`
  position: relative;
  opacity: 1;
  white-space: nowrap;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
      position: absolute;
    `}
`;

export const UTC = styled.div<{ alignment?: 'left' | 'right' }>`
  position: absolute;
  display: inline-block;
  pointer-events: none;
  isolation: isolate;
  top: 0;
  right: 0;
  white-space: nowrap;
  opacity: 0;
  z-index: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  transition: opacity 0.2s;
  /* Reserve the hover padding up front so the wrapper width stays constant
     across hover states; the :hover rule below only toggles opacity. */
  padding: 0 0 0 12px;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
      position: relative;
      padding: 0 12px 0 0;
    `}

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background-color: ${theme.secondary.blendToBackground(25)};
    `}

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    z-index: 2;
    pointer-events: none;
  }

  &:before {
    right: 100%;
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) =>
          theme.id !== 'default' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(50)}
        100%
    );
  }

  &:after {
    left: 100%;
    background-image: linear-gradient(
      90deg,
      ${({ theme }) =>
          theme.id !== 'default' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(50)}
        0%,
      transparent 100%
    );
  }
`;

const Wrapper = styled.div<{ preventHover?: boolean; alignment?: 'left' | 'right' }>`
  position: relative;
  font-size: 12px;
  line-height: 16px;
  height: 16px;
  display: inline-block;

  ${({ preventHover }) =>
    !preventHover &&
    css`
      &:hover {
        ${UTC} {
          opacity: 1;
          width: max-content;
          overflow: visible;
        }

        ${Local} {
          opacity: 0;
        }
      }
    `}
`;

export const AbsoluteTime: React.FC<AbsoluteTimeProps> = ({
  className,
  timestamp,
  preventHover = false,
  alignment = 'right',
}) => {
  return (
    <Wrapper className={className} preventHover={preventHover} alignment={alignment}>
      {!isValidDate(timestamp) ? (
        '-'
      ) : (
        <>
          <Local alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'local' })}</Local>
          <UTC alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'utc' })}</UTC>
        </>
      )}
    </Wrapper>
  );
};
